import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import gameService from '../../../services/gameService';
import {
  Button,
  Spinner,
  Card,
  InputGroup,
  FormControl,
  ProgressBar,
} from 'react-bootstrap';
import {
  FaCoins,
  FaHandPaper,
  FaHandRock,
  FaTrophy,
  FaEquals,
  FaInfoCircle,
  FaHistory,
  FaCrown,
  FaPlay,
  FaArrowLeft,
  FaRedoAlt,
  FaSadTear,
  FaTimesCircle,
} from 'react-icons/fa';
import { AuthContext } from '../../../context/AuthContext';
import './Baccarat.css';

const CHIP_AMOUNTS = [1, 5, 10, 25, 50, 100];

const Baccarat = () => {
  const { t } = useTranslation();
  const [betAmount, setBetAmount] = useState(1);
  const [betSide, setBetSide] = useState(null);
  const [gameResult, setGameResult] = useState(null);
  const [gameInProgress, setGameInProgress] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [history, setHistory] = useState([]);
  const { auth } = useContext(AuthContext);
  const userId = auth.user?.id;
  const [isLoading, setIsLoading] = useState(true);

  const navigate = useNavigate();

  const clampBet = (val) => Math.max(1, Math.floor(Number.isFinite(val) ? val : parseInt(val, 10) || 1));
  const getCardImage = (card) => `${process.env.PUBLIC_URL}/images/games/cards/${card}.png`;

  const handleChipClick = (amount, e) => {
    // Click normal: fija la apuesta. Shift+Click: suma a la apuesta.
    if (e && e.shiftKey) setBetAmount((prev) => clampBet(prev + amount));
    else setBetAmount(clampBet(amount));
  };

  const handlePlay = async () => {
    if (!betSide) { setError(t('baccarat.choose_option')); return; }
    if (betAmount < 1) { setError(t('baccarat.min_bet') || 'Minimum bet is 1'); return; }

    setLoading(true); setError(null); setGameInProgress(true);
    setShowResultModal(false); setGameResult(null);

    try {
      const result = await gameService.playBaccarat(betAmount, betSide);
      setGameResult(result);
      setShowResultModal(true);
    } catch (err) {
      const backendMessage = err?.error || err?.message || t('baccarat.error_processing');
      if (backendMessage?.includes('Insufficient balance') || backendMessage?.includes('Saldo insuficiente')) {
        setError(
          <>
            <span>{t('baccarat.insufficient_balance')}</span><br />
            <Button variant="link" className="p-0 mt-2" onClick={() => navigate('/swapfunds')}>
              {t('baccarat.reload_balance')}
            </Button>
          </>
        );
      } else {
        setError(backendMessage);
      }
    } finally {
      setLoading(false); setGameInProgress(false);
    }
  };

  const handleRestartGame = () => {
    setBetSide(null); setGameResult(null); setError(null);
    setGameInProgress(false); setShowResultModal(false);
  };

  const fetchHistory = async () => {
    if (!userId) return;
    try { setHistory(await gameService.getBaccaratHistory(userId) || []); }
    catch (error) { setError(t('baccarat.error_history')); console.error(error); }
  };

  const getResultIcon = (result) =>
    result === betSide ? (
      <FaTrophy className="baccarat-result-icon baccarat-win-icon" />
    ) : (
      <FaSadTear className="baccarat-result-icon baccarat-lose-icon" />
    );

  const openRulesModal = () => setShowRulesModal(true);
  const closeRulesModal = () => setShowRulesModal(false);
  const openHistoryModal = () => { setShowHistoryModal(true); fetchHistory(); };
  const closeHistoryModal = () => setShowHistoryModal(false);
  const closeResultModal = () => setShowResultModal(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1200);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="baccarat-container d-flex justify-content-center align-items-center">
      {isLoading ? (
        <div className="baccarat-loading-container">
          <h2 className="baccarat-loading-text">{t('baccarat.loading')}</h2>
          <ProgressBar animated now={100} className="baccarat-loading-bar" />
        </div>
      ) : (
        <Card className="text-center baccarat-card shadow-lg">
          <Card.Body>
            <div className="position-relative mb-4">
              {/* Botón Volver fijo */}
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/sessions')}
                className="baccarat-back-button"
                aria-label={t('baccarat.back') || 'Back'}
              >
                <FaArrowLeft />
              </Button>

              <h1 className="baccarat-casino-title text-center">
                <FaCrown className="text-warning me-2" /> {t('baccarat.title')} <FaCrown className="text-warning ms-2" />
              </h1>
            </div>

            {/* Chips de apuesta rápidas */}
            <div className="baccarat-chips" aria-label={t('baccarat.quick_bets') || 'Quick bets'}>
              {CHIP_AMOUNTS.map((amt) => {
                const selected = betAmount === amt && !loading && !gameInProgress;
                return (
                  <button
                    key={amt}
                    type="button"
                    className={`baccarat-chip chip-${amt} ${selected ? 'selected' : ''}`}
                    onClick={(e) => handleChipClick(amt, e)}
                    disabled={loading || gameInProgress}
                    aria-pressed={selected}
                    title="Shift: sumar a la apuesta"
                  >
                    {amt}
                  </button>
                );
              })}
            </div>

            {/* Input de apuesta manual */}
            <div className="mb-3">
              <InputGroup className="baccarat-bet-input-group">
                <InputGroup.Text className="baccarat-bet-input-icon">
                  <FaCoins />
                </InputGroup.Text>
                <FormControl
                  type="number"
                  value={betAmount}
                  onChange={(e) => setBetAmount(clampBet(Number(e.target.value)))}
                  min="1"
                  step="1"
                  required
                  inputMode="numeric"
                  className="baccarat-bet-input"
                  aria-label={t('baccarat.bet') || 'Bet'}
                />
                <InputGroup.Text className="baccarat-bet-input-text">MENEL</InputGroup.Text>
              </InputGroup>
            </div>

            {/* ===== Zonas de apuesta estilo mesa ===== */}
            <div className="baccarat-table" aria-label={t('baccarat.choose_option') || 'Choose bet'}>
              <div className="baccarat-bet-zones">
                {/* Player */}
                <button
                  type="button"
                  className={`baccarat-bet-zone player ${betSide === 'player' ? 'selected' : ''}`}
                  onClick={() => setBetSide('player')}
                  aria-pressed={betSide === 'player'}
                  disabled={loading || gameInProgress}
                  title={t('baccarat.player')}
                >
                  <span className="zone-icon"><FaHandPaper /></span>
                  <span className="zone-label">{t('baccarat.player')}</span>
                </button>

                {/* Separador central decorativo */}
                <div className="baccarat-bet-divider" aria-hidden="true"></div>

                {/* Banker */}
                <button
                  type="button"
                  className={`baccarat-bet-zone banker ${betSide === 'banker' ? 'selected' : ''}`}
                  onClick={() => setBetSide('banker')}
                  aria-pressed={betSide === 'banker'}
                  disabled={loading || gameInProgress}
                  title={t('baccarat.banker')}
                >
                  <span className="zone-icon"><FaHandRock /></span>
                  <span className="zone-label">{t('baccarat.banker')}</span>
                </button>

                {/* Tie (debajo, ocupando toda la fila en móvil) */}
                <button
                  type="button"
                  className={`baccarat-bet-zone tie ${betSide === 'tie' ? 'selected' : ''}`}
                  onClick={() => setBetSide('tie')}
                  aria-pressed={betSide === 'tie'}
                  disabled={loading || gameInProgress}
                  title={t('baccarat.tie')}
                  style={{ gridColumn: '1 / -1', marginTop: 12 }}
                >
                  <span className="zone-icon"><FaEquals /></span>
                  <span className="zone-label">{t('baccarat.tie')}</span>
                </button>
              </div>
            </div>

            {/* Acciones */}
            <div className="d-flex justify-content-around flex-wrap mb-3">
              <Button
                type="button"
                className="baccarat-button baccarat-play-button"
                onClick={handlePlay}
                disabled={!betSide || loading || gameInProgress}
              >
                {loading || gameInProgress ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <>
                    <FaPlay className="me-2" /> {t('baccarat.play')}
                  </>
                )}
              </Button>

              <Button
                type="button"
                className="baccarat-button baccarat-restart-button"
                onClick={handleRestartGame}
                disabled={loading || gameInProgress || !gameResult}
              >
                <FaRedoAlt className="me-2" /> {t('baccarat.restart')}
              </Button>

              <Button
                type="button"
                className="baccarat-button baccarat-history-button"
                onClick={() => { setShowHistoryModal(true); fetchHistory(); }}
                disabled={loading || gameInProgress}
              >
                <FaHistory className="me-2" /> {t('baccarat.history')}
              </Button>

              <Button
                type="button"
                className="baccarat-button baccarat-rules-button"
                onClick={() => setShowRulesModal(true)}
                disabled={loading || gameInProgress}
              >
                <FaInfoCircle className="me-2" /> {t('baccarat.rules')}
              </Button>
            </div>

            {error && (
              <div className="alert alert-danger mt-3 text-center" role="alert">
                {error}
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Modales (resultado, reglas, historial) — sin cambios relevantes */}
      {showResultModal && (
        <div className="baccarat-custom-modal" role="dialog" aria-modal="true">
          <div className="baccarat-modal-content">
            <div className="baccarat-modal-header">
              <h2>{t('baccarat.result_title')}</h2>
              <button className="baccarat-modal-close" onClick={() => setShowResultModal(false)} aria-label={t('baccarat.close') || 'Close'}>
                <FaTimesCircle />
              </button>
            </div>
            <div className="baccarat-modal-body">
              {gameResult && (
                <>
                  <div className="text-center">
                    {gameResult.result === betSide
                      ? <FaTrophy className="baccarat-result-icon baccarat-win-icon" />
                      : <FaSadTear className="baccarat-result-icon baccarat-lose-icon" />}
                    <h3 className="baccarat-result">
                      {gameResult.result === betSide ? t('baccarat.win') : t('baccarat.lose')}
                    </h3>
                    <p className="baccarat-balance">{t('baccarat.new_balance')}: {gameResult.newBalance} MENEL</p>
                  </div>

                  <h5 className="mt-4">{t('baccarat.player_hand')}</h5>
                  <div className="d-flex justify-content-center flex-wrap">
                    {gameResult.playerHand.map((card, index) => (
                      <img key={`p-${index}`} src={getCardImage(card.card)} alt={card.card} className="baccarat-card-image baccarat-animated-card" style={{ animationDelay: `${index * 0.1}s` }} />
                    ))}
                  </div>

                  <h5 className="mt-4">{t('baccarat.banker_hand')}</h5>
                  <div className="d-flex justify-content-center flex-wrap">
                    {gameResult.bankerHand.map((card, index) => (
                      <img key={`b-${index}`} src={getCardImage(card.card)} alt={card.card} className="baccarat-card-image baccarat-animated-card" style={{ animationDelay: `${index * 0.1}s` }} />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showRulesModal && (
        <div className="baccarat-custom-modal" role="dialog" aria-modal="true">
          <div className="baccarat-modal-content">
            <div className="baccarat-modal-header">
              <h2><FaInfoCircle className="me-2" /> {t('baccarat.rules')}</h2>
              <button className="baccarat-modal-close" onClick={() => setShowRulesModal(false)} aria-label={t('baccarat.close') || 'Close'}>
                <FaTimesCircle />
              </button>
            </div>
            <div className="baccarat-modal-body">
              <ul className="list-unstyled">
                <li className="mb-2"><FaHandPaper className="me-2 text-success" /><strong>{t('baccarat.rule_bet_title')}:</strong> {t('baccarat.rule_bet_desc')}</li>
                <li className="mb-2"><FaTrophy className="me-2 text-warning" /><strong>{t('baccarat.rule_value_title')}:</strong> {t('baccarat.rule_value_desc')}</li>
                <li className="mb-2"><FaHandRock className="me-2 text-primary" /><strong>{t('baccarat.rule_win_title')}:</strong> {t('baccarat.rule_win_desc')}</li>
                <li className="mb-2"><FaEquals className="me-2 text-warning" /><strong>{t('baccarat.rule_tie_title')}:</strong> {t('baccarat.rule_tie_desc')}</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {showHistoryModal && (
        <div className="baccarat-custom-modal" role="dialog" aria-modal="true">
          <div className="baccarat-modal-content">
            <div className="baccarat-modal-header">
              <h2><FaHistory className="me-2" /> {t('baccarat.history')}</h2>
              <button className="baccarat-modal-close" onClick={() => setShowHistoryModal(false)} aria-label={t('baccarat.close') || 'Close'}>
                <FaTimesCircle />
              </button>
            </div>
            <div className="baccarat-modal-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {history?.length > 0 ? (
                <ul className="list-group">
                  {history.map((entry, index) => (
                    <li key={index} className="list-group-item">
                      <strong>{t('baccarat.result')}:</strong> {entry.result}<br />
                      <strong>{t('baccarat.bet')}:</strong> {entry.score} MENEL<br />
                      <strong>{t('baccarat.date')}:</strong> {new Date(entry.created_at).toLocaleString()}
                    </li>
                  ))}
                </ul>
              ) : (
                <p>{t('baccarat.no_history')}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Baccarat;

